import React, { useEffect, useRef, useState } from "react";
import { CustomInput } from "components/custom-form-input/CustomInputs";
import renderLatex from "./RenderLatex";
import { generateQuestions, getSignedUrl } from "ServerAPI/serverAPI";
import { Button, Modal, Badge, Spinner } from "react-bootstrap";
import QuizImageUpload from "../QuizImageUpload";
import { useCookies } from "react-cookie";

const PreviewImage = ({ fileName, effectiveCourseId, activeSection }) => {
  const [url, setUrl] = useState(null);
  const [cookies] = useCookies(["userId"]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  useEffect(() => {
    const fetchSignature = async () => {
      try {
        const payload = {
          courseId: effectiveCourseId,
          sectionId: activeSection?.sectionId,
          fileName,
          method: "GET",
          userId: cookies.userId,
        };
        const response = await getSignedUrl(payload);
        if (response.data?.preSignedUrl) {
          setUrl(response.data.preSignedUrl);
        }
      } catch (err) {
        console.error("Failed to load image preview", err);
      }
    };
    if (fileName && effectiveCourseId && activeSection?.sectionId) {
      if (fileName.startsWith("blob:") || fileName.startsWith("http")) {
          setUrl(fileName);
      } else {
          fetchSignature();
      }
    }
  }, [fileName, effectiveCourseId, activeSection, cookies.userId]);

  if (!url) return <div className="text-muted small mt-2"><Spinner animation="border" size="sm" className="me-2" />Loading image...</div>;

  return (
    <>
      <div className="mt-2" onClick={() => setIsFullscreen(true)} style={{ cursor: "pointer" }}>
         <img src={url} alt={fileName} style={{ maxHeight: "200px", maxWidth: "100%", borderRadius: "8px", objectFit: "contain", border: "1px solid #dee2e6", transition: "transform 0.2s" }} onMouseOver={(e) => e.currentTarget.style.transform = "scale(1.02)"} onMouseOut={(e) => e.currentTarget.style.transform = "scale(1)"} />
      </div>

      <Modal show={isFullscreen} onHide={() => setIsFullscreen(false)} fullscreen>
        <Modal.Header closeButton>
          <Modal.Title className="text-truncate">{fileName}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex justify-content-center align-items-center bg-dark p-0">
          <img src={url} alt={fileName} style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }} />
        </Modal.Body>
      </Modal>
    </>
  );
};


const questions = [
  {
    id: 1,
    type: "mcq",
    difficulty: "medium",
    question: "What is considered a billing provider according to the content?",
    explanation:
      "According to the content, a billing provider is 'A healthcare provider who may be an individual, facility, institution, corporate entity, or other organization that supplies health services or items to the patient and submit the medical bills to the insurance company.'",
    source: "data/RCM Training Document.pdf - Billing Providers",
    answers: [
      {
        answer: "A facility that provides healthcare services",
        value: 1,
      },
      {
        answer: "A healthcare provider who supplies services and submits bills",
        value: 2,
      },
      { answer: "A patient who receives healthcare services", value: 3 },
      { answer: "A hospital that bills insurance", value: 4 },
    ],
    correctAnswer: 2,
  },
  {
    id: 2,
    type: "mcq",
    difficulty: "medium",
    question:
      "How is a middle name typically represented in the American name format?",
    explanation:
      "According to the content, the American name format allows for a middle name to be represented with an initial.",
    source: "data/RCM Training Document.pdf - American Name Format",
    answers: [
      { answer: "With a full spelling", value: 1 },
      { answer: "With an initial", value: 2 },
      { answer: "With no representation", value: 3 },
      { answer: "With a nickname", value: 4 },
    ],
    correctAnswer: 2,
  },
  {
    id: 3,
    type: "true_false",
    difficulty: "medium",
    question:
      "Automated or manual transcription is considered medical transcription.",
    explanation:
      "According to the content, medical transcription 'is an associated health profession, which deals in the process of transcription or converting voice-recorded reports as dictated by physicians or other healthcare professionals, into text format.'",
    source: "data/RCM Training Document.pdf - Physician-Patient Encounter",
    answers: ["True", "False"],
    correctAnswer: true,
  },
  {
    id: 4,
    type: "mcq",
    difficulty: "medium",
    question: "Who is a SCP?",
    explanation:
      "A Specialty Care Provider (SCP) is a type of provider the content, with a specific area of expertise.",
    source: "data/RCM Training Document.pdf - Types of Providers",
    answers: [
      { answer: "Primary Care Physician", value: 1 },
      { answer: "Specialty Care Provider", value: 2 },
      { answer: "Cardiologist", value: 3 },
      { answer: "Pediatrician", value: 4 },
    ],
    correctAnswer: 2,
  },
  {
    id: 5,
    type: "fill_blanks",
    difficulty: "medium",
    question: "Martin Luther King Jr. was an __________ minister and activist.",
    explanation:
      "Martin Luther King Jr. is described as an 'American Baptist minister and activist' in the content.",
    source:
      "data/RCM Training Document.pdf - Birthday of Martin Luther King, Jr.",
    answers: [
      { answer: "American Jewish", value: 1 },
      { answer: "American Catholic", value: 2 },
      { answer: "Kenyan Muslim", value: 3 },
      { answer: "American Baptist", value: 4 },
    ],
    answer: "American Baptist",
    correctAnswer: 4,
  },
  {
    id: 6,
    type: "mcq",
    difficulty: "medium",
    question: "What is the purpose of a Taxonomy code?",
    explanation:
      "Taxonomy code is used for the 'identification of healthcare providers and services' according to the content.",
    source: "data/RCM Training Document.pdf - TAXONOMY CODE",
    answers: [
      { answer: "Payment processing", value: 1 },
      { answer: "Insurance registration", value: 2 },
      { answer: "Provider identification", value: 3 },
      { answer: "None of the above", value: 4 },
    ],
    correctAnswer: 3,
  },
  {
    id: 7,
    type: "mcq",
    difficulty: "medium",
    question: "What type of payer is Federal?",
    explanation: "Federal is considered a type of payer in the content.",
    source: "data/RCM Training Document.pdf - Types of Payers",
    answers: [
      { answer: "Commercial", value: 1 },
      { answer: "Managed Care Organization", value: 2 },
      { answer: "Federal", value: 3 },
      { answer: "Liability", value: 4 },
    ],
    correctAnswer: 3,
  },
];

export const DisplayBulkUploadedQnA = ({
  handleGeneratedQuizSubmit,
  passingPerctage,
  responseData,
  finalAssessment,
  isBulkQuizUpload = false,
  quizTimer = 0,
  effectiveCourseId,
  activeSection,
}) => {
  // const [responseData, setResponseData] = useState(questions);
  const containerRefs = useRef({});
  // Add a ref to keep track of the last temporary ID
  const lastTempIdRef = useRef(0);

  // Function to generate temporary question ID
  const generateTempQuestionId = () => {
    lastTempIdRef.current += 1;
    return `temp_${lastTempIdRef.current}`;
  };

  const [editableData, setEditableData] = useState(() => {
    const groupedData = {};
    responseData.forEach((question) => {
      const key = `${question.type}_${question.difficulty}`;
      if (!groupedData[key]) {
        groupedData[key] = [];
      }
      // Add temporary question_id if it doesn't exist
      const questionWithId = {
        ...question,
        id: question.id || generateTempQuestionId(),
      };
      groupedData[key].push(questionWithId);
    });
    return groupedData;
  });

  useEffect(() => {
    const groupedData = {};
    responseData.forEach((question) => {
      const key = `${question.type}_${question.difficulty}`;
      if (!groupedData[key]) {
        groupedData[key] = [];
      }
      // Add temporary question_id if it doesn't exist
      const questionWithId = {
        ...question,
        id: question.id || generateTempQuestionId(),
      };
      groupedData[key].push(questionWithId);
    });
    return setEditableData(groupedData);
  }, [responseData]);

  const [editingQuestion, setEditingQuestion] = useState(null);
  const [originalQuestion, setOriginalQuestion] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const uploadImageRef = useRef(null);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [uploadModalState, setUploadModalState] = useState({
    isOpen: false,
    sectionKey: null,
    targetId: null,
    targetType: null,
    optionIndex: null,
    existingImages: [],
  });
  const [hasPendingUploads, setHasPendingUploads] = useState(false);

  const handleOpenUploadModal = (sectionKey, questionId, targetType, optionIndex = null, existingImages = []) => {
    setUploadModalState({
      isOpen: true,
      sectionKey,
      targetId: questionId,
      targetType,
      optionIndex,
      existingImages: existingImages || [],
    });
    setHasPendingUploads(false);
  };

  const handleCloseUploadModal = () => {
    setUploadModalState({
      isOpen: false,
      sectionKey: null,
      targetId: null,
      targetType: null,
      optionIndex: null,
      existingImages: [],
    });
  };

  const handleSaveImages = async () => {
    if (uploadImageRef.current) {
      setIsUploadingImages(true);
      try {
        const uploadedImages = await uploadImageRef.current.uploadImages();
        const urls = uploadedImages.map(img => img.url?.split("?")[0].split("/").pop() || img.file?.name).filter(Boolean);
        
        setEditableData((prevData) => ({
          ...prevData,
          [uploadModalState.sectionKey]: prevData[uploadModalState.sectionKey].map((q) => {
            if (q.id === uploadModalState.targetId) {
              if (uploadModalState.targetType === "question") {
                return { ...q, imageQuestion: urls };
              } else if (uploadModalState.targetType === "option" && q.answers) {
                const updatedAnswers = [...q.answers];
                updatedAnswers[uploadModalState.optionIndex] = { 
                  ...updatedAnswers[uploadModalState.optionIndex], 
                  imageOptionName: urls 
                };
                return { ...q, answers: updatedAnswers };
              }
            }
            return q;
          }),
        }));
        // Do not close the modal here to allow users to view the 'Image Preview' section
      } catch (error) {
        console.error("Failed to upload images", error);
      } finally {
        setIsUploadingImages(false);
      }
    }
  };

  const handleImageSync = (images) => {
    if (!uploadModalState.isOpen || !uploadModalState.targetId) return;

    const urls = images.map(img => img.url?.split("?")[0].split("/").pop() || img.file?.name).filter(Boolean);
    
    setEditableData((prevData) => {
      const currentSectionData = prevData[uploadModalState.sectionKey];
      if (!currentSectionData) return prevData;

      return {
        ...prevData,
        [uploadModalState.sectionKey]: currentSectionData.map((q) => {
          if (q.id === uploadModalState.targetId) {
            if (uploadModalState.targetType === "question") {
              return { ...q, imageQuestion: urls };
            } else if (uploadModalState.targetType === "option" && q.answers) {
              const updatedAnswers = [...q.answers];
              updatedAnswers[uploadModalState.optionIndex] = { 
                ...updatedAnswers[uploadModalState.optionIndex], 
                imageOptionName: urls 
              };
              return { ...q, answers: updatedAnswers };
            }
          }
          return q;
        }),
      };
    });
  };

  const handleEditClick = (question) => {
    // If already editing a different question, revert changes before switching
    if (editingQuestion && editingQuestion !== question) {
      handleCancelClick();
    }

    const sectionKey = `${question.type}_${question.difficulty}`;
    const questionToEdit = editableData[sectionKey].find(
      (q) => q.id === question.id
    );

    setEditingQuestion(questionToEdit);
    setOriginalQuestion({ ...questionToEdit });
  };

  const validateQuestion = (question) => {
    if (!question.question || question.question.trim() === "") {
      return { isValid: false, message: "Question text is required" };
    }

    if (
      !question.correctAnswer ||
      (typeof question.correctAnswer === "string" &&
        question.correctAnswer.trim() === "")
    ) {
      return { isValid: false, message: "Answer is required" };
    }

    if (question.answers && Array.isArray(question.answers)) {
      const emptyOptions = question.answers.some(
        (opt) => !opt.answer || opt.answer.trim() === ""
      );
      if (emptyOptions) {
        return {
          isValid: false,
          message: "All options must be filled out",
        };
      }
    }

    return { isValid: true };
  };

  const handleSaveClick = () => {
    if (!editingQuestion) return;

    const sectionKey = `${editingQuestion.type}_${editingQuestion.difficulty}`;
    const currentQuestion = editableData[sectionKey].find(
      (q) => q.id === editingQuestion.id
    );

    const validation = validateQuestion(currentQuestion);
    if (!validation.isValid) {
      alert(validation.message);
      return;
    }

    setHasUnsavedChanges(true);
    setEditingQuestion(null);
    setOriginalQuestion(null);
  };

  const handleCancelClick = () => {
    if (editingQuestion && originalQuestion) {
      const sectionKey = `${editingQuestion.type}_${editingQuestion.difficulty}`;

      setEditableData((prevData) => ({
        ...prevData,
        [sectionKey]: prevData[sectionKey].map((q) =>
          q.id === editingQuestion.id ? { ...originalQuestion } : q
        ),
      }));
    }
    setEditingQuestion(null);
    setOriginalQuestion(null);
  };

  const handleInputChange = (field, value) => {
    console.log(field, value, typeof value);

    if (!editingQuestion) return;

    const sectionKey = `${editingQuestion.type}_${editingQuestion.difficulty}`;

    setEditableData((prevData) => ({
      ...prevData,
      [sectionKey]: prevData[sectionKey].map((q) =>
        q.id === editingQuestion.id ? { ...q, [field]: value } : q
      ),
    }));
  };

  const handleUpdateOptions = (options, optionToUpdate, e) => {
    const updatedOptions = options?.map((opt) => {
      return opt.value === optionToUpdate.value
        ? {
            ...opt,
            answer: e.target.value,
          }
        : opt;
    });
    handleInputChange(
      "answers",

      updatedOptions
    );
  };

  const setRubricField = (idx, field, value) => {
    if (!editingQuestion) return;
    const sectionKey = `${editingQuestion.type}_${editingQuestion.difficulty}`;

    setEditableData((prevData) => ({
      ...prevData,
      [sectionKey]: prevData[sectionKey].map((q) =>
        q.id === editingQuestion.id
          ? {
              ...q,
              rubric: (q.rubric || []).map((r, i) =>
                i === idx
                  ? { ...r, [field]: field === "marks" ? Number(value) : value }
                  : r
              ),
            }
          : q
      ),
    }));
  };

  const addRubricCriterion = () => {
    if (!editingQuestion) return;
    const sectionKey = `${editingQuestion.type}_${editingQuestion.difficulty}`;

    setEditableData((prevData) => ({
      ...prevData,
      [sectionKey]: prevData[sectionKey].map((q) =>
        q.id === editingQuestion.id
          ? {
              ...q,
              rubric: [
                ...(q.rubric || []),
                { criterion: "", description: "", marks: 0, partial_credit_guidelines: "" },
              ],
            }
          : q
      ),
    }));
  };

  const removeRubricCriterion = (idx) => {
    if (!editingQuestion) return;
    const sectionKey = `${editingQuestion.type}_${editingQuestion.difficulty}`;

    setEditableData((prevData) => ({
      ...prevData,
      [sectionKey]: prevData[sectionKey].map((q) =>
        q.id === editingQuestion.id
          ? { ...q, rubric: (q.rubric || []).filter((_, i) => i !== idx) }
          : q
      ),
    }));
  };

  const renderQuestionGroup = (sectionKey, globalStartIndex) => {
    const questions = editableData[sectionKey];

    const capitalizeFirst = (str) =>
      str === "null" || !str ? "" : str.charAt(0).toUpperCase() + str.slice(1);

    const typePart = sectionKey.split("_")[0];
    let title = "";
    if (typePart === "mcq") {
      title = "MCQ";
    } else if (typePart === "true_false") {
      title = "True/False";
    } else if (typePart === "fib") {
      title = "Fill in the Blanks";
    } else if (typePart === "subjective") {
      title = "Subjective";
    } else {
      title = capitalizeFirst(typePart);
    }
    title += " Questions";
      
    return (
      <div key={sectionKey} className="mb-4 border-0 ">
        <div className="">
          {isBulkQuizUpload ? null : (
            <>
              {" "}
              <h2 className=" text-center mb-4">
{title} Questions              </h2>
            </>
          )}

          <ul
            ref={(el) => (containerRefs.current[sectionKey] = el)}
            className="list-group list-group-flush"
          >
            {questions.map((q, index) => {
              const isEditing = editingQuestion?.id === q.id;

              return (
                <li
                  key={index}
                  data-question-id={q.id}
                  className="list-group-item position-relative"
                >
                  {/* Drag handle */}
                  {/* <div className="position-absolute start-0 top-50 translate-middle-y cursor-move drag-handle">
                                        ☰
                                    </div> */}

                  <div className="d-flex justify-content-between">
                    <div className="flex-grow-1">
                      <div className="mb-3">
                        {/* Question */}
                        {isEditing ? (
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <div className="flex-grow-1">
                              <CustomInput
                                type="textarea"
                                id="text-area"
                                label="Question"
                                value={q.question}
                                onChange={(e) =>
                                  handleInputChange("question", e.target.value)
                                }
                                placeholder="Enter your question"
                                maxChars={500}
                                rows={3}
                              />
                            </div>
                            <Button 
                              variant={q.imageQuestion?.length > 0 ? "primary" : "outline-secondary"}
                              className="align-self-center mt-3"
                              onClick={() => handleOpenUploadModal(sectionKey, q.id, "question", null, q.imageQuestion || [])}
                            >
                              <i className={`bi bi-image ${q.imageQuestion?.length > 0 ? "text-white" : "text-muted"}`}></i>
                            </Button>
                          </div>
                        ) : (
                          <div className="mb-3">
                            <h5 className="fw-bold d-flex align-items-center flex-wrap gap-2 mb-1">
                              <span>Q{globalStartIndex + index + 1}:{" "}
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: renderLatex(q.question),
                                }}
                              /></span>
                            </h5>
                            <div className="text-muted small fst-italic mb-2">(Click on edit to add an image)</div>
                            {q.imageQuestion?.length > 0 && (
                                <div className="d-flex flex-wrap gap-3">
                                  {q.imageQuestion.map((fileName, i) => (
                                    <PreviewImage key={i} fileName={fileName} effectiveCourseId={effectiveCourseId} activeSection={activeSection} />
                                  ))}
                                </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Options */}
                      {q.answers && Array.isArray(q.answers) && (
                        <div className="mb-3">
                          {isEditing && q.type != "true_false" ? (
                            q.answers.map((option, idx) => (
                              <div key={idx} className="mb-2 d-flex gap-2 align-items-center">
                                <div className="flex-grow-1">
                                  <CustomInput
                                    type="text"
                                    label={option.value}
                                    value={option.answer}
                                    wrapperClassName="d-flex gap-3 align-items-center"
                                    onChange={(e) =>
                                      handleUpdateOptions(q.answers, option, e)
                                    }
                                    placeholder="Enter option"
                                  />
                                </div>
                                <Button 
                                  variant={option.imageOptionName?.length > 0 ? "primary" : "outline-secondary"}
                                  className="align-self-center mt-3"
                                  onClick={() => handleOpenUploadModal(sectionKey, q.id, "option", idx, option.imageOptionName || [])}
                                >
                                  <i className={`bi bi-image ${option.imageOptionName?.length > 0 ? "text-white" : "text-muted"}`}></i>
                                </Button>
                              </div>
                            ))
                          ) : (
                            <ul className="list-group">
                              {q.answers.map((option, idx) => (
                                <li key={idx} className="list-group-item">
                                  <div className="d-flex justify-content-between align-items-start">
                                    <span>
                                      {option.value && option.value + ". "}
                                      <span
                                        dangerouslySetInnerHTML={{
                                          __html: renderLatex(
                                            option.answer || option
                                          ),
                                        }}
                                      />
                                    </span>
                                  </div>
                                  {option.imageOptionName?.length > 0 && (
                                    <div className="d-flex flex-wrap gap-3 mt-2">
                                      {option.imageOptionName.map((fileName, i) => (
                                        <PreviewImage key={i} fileName={fileName} effectiveCourseId={effectiveCourseId} activeSection={activeSection} />
                                      ))}
                                    </div>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}

                      {/* Answer */}
                      <div className="mb-3">
                        {isEditing ? (
                          q.type === "mcq" || q.type === "true_false" ? (
                            <CustomInput
                              type="number"
                              label="Answer"
                              value={q.correctAnswer}
                              onChange={(e) => {
                                handleInputChange(
                                  "correctAnswer",
                                  isNaN(e.target.valueAsNumber)
                                    ? null
                                    : e.target.valueAsNumber
                                );
                              }}
                            />
                          ) : (
                            <CustomInput
                              type="textarea"
                              id="text-area-answer"
                              label="Answer"
                              value={q.correctAnswer || ""}
                              onChange={(e) => {
                                handleInputChange("correctAnswer", e.target.value);
                              }}
                              placeholder="Enter correct answer"
                              rows={2}
                            />
                          )
                        ) : (
                          <div
                            className=""
                            style={{
                              // border: "2px solid #23479f",
                              borderRadius: "7px",
                              backgroundColor: "#23479f1a",
                              padding: "10px 20px",
                              width: "100%",
                            }}
                          >
                            <strong>Answer: </strong>
                            <span>{q.correctAnswer}</span>
                          </div>
                        )}
                      </div>

                      {/* Explanation */}
                      {(isEditing || q.explanation) && (
                        <div className="mb-3">
                          {isEditing ? (
                            <CustomInput
                              type="textarea"
                              id="text-area-explanation"
                              label="Explanation"
                              value={q.explanation || ""}
                              onChange={(e) =>
                                handleInputChange("explanation", e.target.value)
                              }
                              placeholder="Enter explanation"
                              maxChars={2000}
                              rows={3}
                            />
                          ) : (
                            <div
                              className=""
                              style={{
                                borderRadius: "7px",
                                backgroundColor: "#23479f1a",
                                padding: "10px 20px",
                                width: "100%",
                              }}
                            >
                              <strong>Explanation: </strong>
                              <span>{q.explanation}</span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Marks */}
                      {(isEditing || q.marks !== undefined) && (
                        <div className="mb-3">
                          {isEditing ? (
                            <CustomInput
                              type="number"
                              label="Marks"
                              value={q.marks !== undefined ? q.marks : ""}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                handleInputChange("marks", isNaN(val) ? "" : val);
                              }}
                              placeholder="Enter marks"
                            />
                          ) : (
                            <div
                              className=""
                              style={{
                                borderRadius: "7px",
                                backgroundColor: "#23479f1a",
                                padding: "10px 20px",
                                width: "100%",
                              }}
                            >
                              <strong>Marks: </strong>
                              <span>{q.marks}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Rubric */}
                      {(isEditing || (Array.isArray(q.rubric) && q.rubric.length > 0)) && (
                        <div className="mb-3">
                          {isEditing ? (
                            <div className="border rounded-3 p-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <strong>Rubric</strong>
                                <Button variant="outline-primary" size="sm" onClick={addRubricCriterion}>
                                  + Add Criterion
                                </Button>
                              </div>

                              {(!q.rubric || q.rubric.length === 0) && (
                                <div className="text-muted small mb-2">No rubric criteria yet.</div>
                              )}

                              {(q.rubric || []).map((r, ridx) => (
                                <div key={ridx} className="border rounded-3 p-3 mb-2 bg-light">
                                  <div className="d-flex justify-content-between align-items-start gap-2">
                                    <div className="flex-grow-1">
                                      <CustomInput
                                        type="text"
                                        label="Criterion"
                                        value={r.criterion || ""}
                                        onChange={(e) => setRubricField(ridx, "criterion", e.target.value)}
                                        placeholder="Criterion name"
                                      />
                                      <CustomInput
                                        type="textarea"
                                        label="Description"
                                        value={r.description || ""}
                                        onChange={(e) => setRubricField(ridx, "description", e.target.value)}
                                        placeholder="Describe what this criterion evaluates"
                                        rows={2}
                                      />
                                      <CustomInput
                                        type="number"
                                        label="Marks"
                                        value={r.marks ?? 0}
                                        onChange={(e) => setRubricField(ridx, "marks", e.target.value)}
                                        placeholder="Marks"
                                      />
                                      <CustomInput
                                        type="textarea"
                                        label="Partial Credit Guidelines"
                                        value={r.partial_credit_guidelines || ""}
                                        onChange={(e) => setRubricField(ridx, "partial_credit_guidelines", e.target.value)}
                                        placeholder="Explain how partial credit should be awarded"
                                        rows={2}
                                      />
                                    </div>
                                    <Button variant="outline-danger" size="sm" onClick={() => removeRubricCriterion(ridx)}>
                                      &times;
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div>
                              <strong>Rubric:</strong>
                              <div className="table-responsive mt-1">
                                <table className="table table-sm table-bordered mb-0" style={{ fontSize: "0.85em" }}>
                                  <thead className="table-light">
                                    <tr>
                                      <th>Criterion</th>
                                      <th>Description</th>
                                      <th style={{ width: "70px" }}>Marks</th>
                                      <th>Partial Credit Guidelines</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {q.rubric.map((r, ridx) => (
                                      <tr key={ridx}>
                                        <td className="fw-semibold">{r.criterion}</td>
                                        <td>{r.description}</td>
                                        <td>{r.marks}</td>
                                        <td>{r.partial_credit_guidelines}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Button section on the right */}
                    <div className="d-flex flex-column align-items-end ms-3">
                      {isEditing ? (
                        <>
                          <Button
                            onClick={handleSaveClick}
                            variant="success"
                            className="rounded-circle mb-2"
                            style={{
                              padding: "8px 14px",
                              backgroundColor: "#26bf941a",
                              border: 0,
                              color: "#26bf94",
                            }}
                          >
                            <i className="bi bi-check-lg"></i>
                          </Button>
                          <Button
                            onClick={handleCancelClick}
                            variant="danger"
                            className="rounded-circle mb-2"
                            style={{
                              padding: "8px 14px",
                              backgroundColor: "#e6533c1a",
                              border: 0,
                              color: "#e6533c",
                            }}
                          >
                            <i className="bi bi-x"></i>
                          </Button>
                          {/* Shuffle button */}
                          {/* {isEditing &&
                                                        q.options &&
                                                        q.options.length >
                                                            0 && (
                                                            <Button
                                                                onClick={
                                                                    handleShuffleClick
                                                                }
                                                                variant="warning"
                                                                className="rounded-circle"
                                                                style={{
                                                                    padding:
                                                                        "8px 14px",
                                                                }}
                                                            >
                                                                <i className="bi bi-shuffle"></i>
                                                            </Button>
                                                        )} */}
                        </>
                      ) : (
                        <>
                          <Button
                            variant="info"
                            onClick={() => handleEditClick(q)}
                            className="rounded-circle mb-2"
                            style={{
                              padding: "8px 14px",
                              backgroundColor: "#49b6f51a",
                              border: 0,
                              color: "#49b6f5",
                            }}
                          >
                            <i className="bi bi-pencil-square"></i>
                          </Button>
                          {/* <Button
                                                        variant="danger"
                                                        className="rounded-circle"
                                                        style={{
                                                            padding: "8px 14px",
                                                        }}
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </Button> */}
                        </>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    );
  };

  // Render method with grouped questions
  const renderQuestions = () => {
    let globalCounter = 0;
    return Object.keys(editableData).map((sectionKey) => {
      const sectionStartIndex = globalCounter;
      globalCounter += editableData[sectionKey]?.length || 0;
      return renderQuestionGroup(sectionKey, sectionStartIndex);
    });
  };

  return (
    <div>
      <div>
        {renderQuestions()}
        <Button
          onClick={() =>
            handleGeneratedQuizSubmit(
              editableData,
              Number(passingPerctage),
              finalAssessment,
              quizTimer
            )
          }
        >
          Next
        </Button>
      </div>

      <Modal show={uploadModalState.isOpen} onHide={handleCloseUploadModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Upload Images for {uploadModalState.targetType === "question" ? "Question" : `Option ${uploadModalState.optionIndex + 1}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {uploadModalState.isOpen && (
            <QuizImageUpload
              ref={uploadImageRef}
              onImageUpload={handleImageSync}
              onPendingFilesChange={setHasPendingUploads}
              existingImages={uploadModalState.existingImages}
              effectiveCourseId={effectiveCourseId}
              activeSection={activeSection}
              editQuiz={true}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseUploadModal} disabled={isUploadingImages}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSaveImages} disabled={isUploadingImages || !hasPendingUploads}>
            {isUploadingImages ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Uploading...
              </>
            ) : "Upload"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
